/**
 * Org board (spec page 6, approved): the real org chart. Robin (Executive) at
 * the top, Nick (COO) and the directors under him, each division's box under
 * its leader with open count + urgent flags. Click a box to expand its
 * requests. Company-wide ("All") requests sit in a band across the top.
 * Tree shape comes from config.ts (DIVISIONS.reportsTo + ORG_NODES).
 */
import { useState } from 'react';
import { DIVISIONS, ORG_NODES, score, tier, type Settings } from '../config';
import type { Req } from '../lib/data';
import { lineupOrder } from '../lib/order';
import { applyFilter, Filters, NO_FILTER, type Filter } from '../parts';
import { StatusTag, TierBadge } from '../ui';
import { Guide } from '../guide';

interface Node { id: string; label: string; leader?: string; isDivision: boolean; children: Node[] }

function buildTree(): Node {
  const nodes = new Map<string, Node>();
  for (const d of DIVISIONS) if (d.id !== 'all') nodes.set(d.id, { id: d.id, label: d.label, leader: d.leader, isDivision: true, children: [] });
  for (const o of ORG_NODES) nodes.set(o.id, { id: o.id, label: o.label, leader: o.leader, isDivision: false, children: [] });
  const parentOf = (id: string) => DIVISIONS.find((d) => d.id === id)?.reportsTo ?? ORG_NODES.find((o) => o.id === id)?.reportsTo;
  for (const [id, n] of nodes) {
    const p = parentOf(id);
    if (p && nodes.has(p)) nodes.get(p)!.children.push(n);
  }
  return nodes.get('executive')!;
}
const TREE = buildTree();

export function OrgBoard({ items, settings, go }: { items: Req[]; settings: Settings; go: (to: string) => void }) {
  const [filter, setFilter] = useState<Filter>(NO_FILTER);
  const [open, setOpen] = useState<string | null>(null);
  const live = applyFilter(items.filter((r) => r.status !== 'completed'), { ...filter, division: '' });
  const inDiv = (id: string) => lineupOrder(live.filter((r) => r.divisions.includes(id as never)));
  const companyWide = inDiv('all');
  const hot = (r: Req) => ['critical', 'high'].includes(tier(score(r.impact, r.urgency), settings.tiers));

  const box = (n: Node) => {
    const reqs = n.isDivision ? inDiv(n.id) : [];
    const urgent = reqs.filter(hot).length;
    const expanded = open === n.id;
    return (
      <div className={`org-box ${n.isDivision ? '' : 'person'} ${expanded ? 'expanded' : ''} ${urgent ? 'has-urgent' : ''}`}
        onClick={() => n.isDivision && setOpen(expanded ? null : n.id)}>
        <div className="org-label">{n.label}</div>
        {n.leader && <div className="org-leader">{n.leader}</div>}
        {n.isDivision && (
          <div className="org-counts">
            <span><b>{reqs.length}</b> open</span>
            {urgent > 0 && <span className="urgent">{urgent} urgent</span>}
          </div>
        )}
        {expanded && (
          <div className="org-reqs" onClick={(e) => e.stopPropagation()}>
            {!reqs.length && <div className="muted small">Nothing open.</div>}
            {reqs.slice(0, 8).map((r) => (
              <div key={r.id} className="org-req" onClick={() => go(`/r/${r.id}`)}>
                <span>{r.title}</span>
                <span className="org-req-tags"><TierBadge r={r} settings={settings} /><StatusTag status={r.status} /></span>
              </div>
            ))}
            {reqs.length > 8 && <button className="linkish" onClick={() => go(`/kanban?division=${n.id}`)}>All {reqs.length} on the Kanban</button>}
          </div>
        )}
      </div>
    );
  };

  const branch = (n: Node): React.ReactNode => (
    <li key={n.id}>
      {box(n)}
      {n.children.length > 0 && <ul>{n.children.map(branch)}</ul>}
    </li>
  );

  return (
    <div>
      <div className="toolbar">
        <h2 className="page-title">Org board</h2>
        <Filters items={items} value={filter} onChange={(f) => setFilter({ ...f, division: '' })} />
        <span className="spacer" />
        <span className="muted small">Click a division to see its requests.</span>
      </div>

      <Guide page="org" />
      <div className="org-band">
        <div className="org-band-head">Company-wide <span className="count">{companyWide.length}</span></div>
        <div className="org-band-items">
          {companyWide.map((r) => (
            <button key={r.id} className="band-chip" onClick={() => go(`/r/${r.id}`)}>{r.title}</button>
          ))}
          {!companyWide.length && <span className="muted small">No company-wide requests open.</span>}
        </div>
      </div>

      <div className="org-scroll">
        <ul className="org-tree">{branch(TREE)}</ul>
      </div>
    </div>
  );
}
